import type { ReactNode } from 'react';
import { copyToClipboard } from '@gonfalon/dom/copyToClipboard';
import { msg } from '@gonfalon/messages/msg';

import type {
  DataTableAction,
  DataTableCellFilter,
  DataTableCellValue,
  DataTableColumn,
  DataTableColumnRef,
  SortValue,
} from '../types';

type Options<T> = {
  content: ReactNode;
  getDisplayedText: () => string | undefined;
  onCellFilter?: (filter: DataTableCellFilter<T>) => void;
};

const toCellValue = (value: SortValue | ReactNode): DataTableCellValue | undefined => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }
  if ((typeof value === 'string' && value !== '') || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  return undefined;
};

export function getDefaultCellActions<T>(
  column: DataTableColumn<T> & { ref: DataTableColumnRef },
  row: T,
  { content, getDisplayedText, onCellFilter }: Options<T>,
): Array<DataTableAction<T>> {
  const textContent = typeof content === 'string' || typeof content === 'number' ? toCellValue(content) : undefined;
  const rendersMarkup = typeof content === 'object' && content !== null;
  const value = column.getCellValue
    ? toCellValue(column.getCellValue(row))
    : (toCellValue(column.getSortValue?.(row)) ?? textContent);
  const actions: Array<DataTableAction<T>> = [];

  if (textContent !== undefined || value !== undefined) {
    actions.push({
      id: 'copy',
      label: msg('ui.dataTable.cellActions.copy'),
      icon: 'copy-content',
      onAction: () => {
        void copyToClipboard(String(textContent ?? (rendersMarkup ? getDisplayedText() : undefined) ?? value));
      },
    });
  }

  if (onCellFilter && value !== undefined) {
    actions.push(
      {
        id: 'filter',
        label: msg('ui.dataTable.cellActions.filter'),
        icon: 'filter',
        onAction: () => onCellFilter({ column: column.ref, value, row, operator: 'include' }),
      },
      {
        id: 'exclude',
        label: msg('ui.dataTable.cellActions.exclude'),
        icon: 'minus',
        onAction: () => onCellFilter({ column: column.ref, value, row, operator: 'exclude' }),
      },
    );
  }

  return actions;
}
